/**
 * ProfilePicture - Profile picture component with cropping functionality
 */
import { useState, useCallback } from "react";
import { toast } from "sonner";
import customToast from "../../utils/customToast";
import { Camera, User, ZoomIn } from "lucide-react";
import { pickPhotoFromLibrary } from "../../utils/photoPicker";
import Cropper from 'react-easy-crop';
import { Button } from "../ui/button";
import { Slider } from "../ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { createCroppedImage } from "../../utils/imageHelpers";
import { isPurpleOn } from '../../utils/purple';

const ProfilePicture = ({ user, userData, appSettings, onUpload, theme = 'pink', size = 'md', showBorder = true }) => {
  // Priority: userData.profile_picture (saved) > appSettings.profile_image (temp) > user.picture (Google)
  const profileImage = userData?.profile_picture || appSettings?.profile_image || user?.picture;
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  
  // Size classes
  const sizeClasses = {
    sm: 'w-10 h-10',
    md: 'w-20 h-20',
    lg: 'w-24 h-24'
  };
  
  const iconSizes = {
    sm: 'w-5 h-5',
    md: 'w-10 h-10',
    lg: 'w-12 h-12'
  };
  
  // Theme-based border colors
  const getBorderColor = () => {
    if (!showBorder) return 'border-transparent';
    if (theme === 'warm') return 'border-[#2D5A3D]';
    if (theme === 'blue') return 'border-[#6366f1]';
    if (theme === 'sage' || theme === 'green') return 'border-[#25C58D]';
    if (isPurpleOn()) return 'border-[#D3C3F4]';
    return 'border-[#F9A7D3]';
  };

  const handleImageSelect = async () => {
    try {
      const dataUrl = await pickPhotoFromLibrary();
      if (dataUrl) {
        setImageToCrop(dataUrl);
        setShowCropper(true);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
      }
    } catch (e) {
      customToast.error("Failed to load photo", "Please try again.");
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSaveCrop = async () => {
    if (imageToCrop && croppedAreaPixels) {
      try {
        const croppedImage = await createCroppedImage(imageToCrop, croppedAreaPixels);
        await onUpload(croppedImage);
        customToast.success("Profile picture updated!");
        setShowCropper(false);
        setImageToCrop(null);
      } catch (error) {
        customToast.error("Crop failed", "Failed to crop image");
      }
    }
  };

  const handleCancel = () => {
    setShowCropper(false);
    setImageToCrop(null);
  };

  return (
    <>
      <div className="relative group">
        <button type="button" onClick={handleImageSelect} className="cursor-pointer block text-left" data-testid="profile-picture">
          <div className={`${sizeClasses[size]} rounded-full ${showBorder ? 'border-4' : 'border-0'} ${getBorderColor()} overflow-hidden theme-bg-100 flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-105`}>
            {profileImage ? (
              <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <User className={`${iconSizes[size]} theme-text-400`} />
            )}
          </div>
          {size !== 'sm' && (
            <div className="absolute bottom-0 right-0 w-7 h-7 theme-bg-500 wy-pp-badge rounded-full flex items-center justify-center border-2 border-white shadow-md group-hover:theme-bg-600 transition-colors">
              <Camera className="w-3.5 h-3.5 text-white" />
            </div>
          )}
        </button>
      </div>

      {/* Crop Dialog */}
      <Dialog open={showCropper} onOpenChange={setShowCropper}>
        <DialogContent className="sm:max-w-md wy-crop">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ZoomIn className="w-5 h-5 theme-text-500" />
              Crop Profile Picture
            </DialogTitle>
          </DialogHeader>
          
          <div className="relative w-full h-64 bg-gray-900 rounded-lg overflow-hidden">
            {imageToCrop && (
              <Cropper
                image={imageToCrop}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            )}
          </div>
          
          <div className="flex items-center gap-3 px-2">
            <ZoomIn className="w-4 h-4 text-gray-400" />
            <Slider
              value={[zoom]}
              min={1}
              max={3}
              step={0.1}
              onValueChange={(value) => setZoom(value[0])}
              className="flex-1"
            />
            <span className="text-sm text-gray-500 w-12">{zoom.toFixed(1)}x</span>
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleSaveCrop} className="theme-bg-500 hover:theme-bg-600 text-white wy-crop-save">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProfilePicture;
